import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import Modal from '@/components/common/Modal';
import { HELPDESK_URL, SUPPORT_EMAIL } from './contactSupport';

interface ContactSupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Open/close state for the Contact Support modal, owned by whichever shell surface hosts the
 * menu that triggers it (staff rail, client rail, mobile shells). Each keeps its own copy —
 * ResponsiveShell/pickShell mount exactly one shell at a time, so they never collide. State
 * can't live in MoreSheet itself: the sheet closes on selection, which would unmount the modal.
 */
export const useContactSupport = () => {
  const [isContactSupportOpen, setOpen] = useState(false);
  return {
    isContactSupportOpen,
    openContactSupport: useCallback(() => setOpen(true), []),
    closeContactSupport: useCallback(() => setOpen(false), []),
  };
};

const linkCls = 'tw-font-semibold tw-text-ds-primary tw-underline';

export const ContactSupportModal = ({
  isOpen,
  onClose,
}: ContactSupportModalProps) => {
  // app.js dispatches UPDATE_SETTINGS with packageJson.version on boot; the settings reducer
  // seeds '0.0.0', so this always renders something even before that dispatch lands.
  const appVersion =
    useSelector((s: any) => s.settings?.app_version) || '0.0.0';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Contact Support" size="sm">
      <div className="tw-font-poppins tw-text-[14px] tw-text-ds-ink-soft">
        <p className="tw-m-0">
          Submit a ticket through our{' '}
          <a
            href={HELPDESK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={linkCls}
          >
            Helpdesk
          </a>
        </p>
        <p className="tw-my-[6px]">or</p>
        <p className="tw-m-0">
          Email us at{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className={linkCls}>
            {SUPPORT_EMAIL}
          </a>
        </p>
        <p className="tw-mt-[18px] tw-mb-0 tw-text-[12.5px] tw-text-ds-ink-mute">
          App Version: {appVersion}
        </p>
      </div>
    </Modal>
  );
};
